import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import React, { useState } from "react";
import {
  normaliseFuelType,
  SUPPLIER_FUEL_TYPE,
} from "../../constants/tariffInfo";
import { useTariff } from "../../context/TariffContext";
import EN from "../../locales/en";
import SwitchNowButton from "../SwitchNowButton";
import Tabs from "../Tabs";
import QuoteTariffEstimatedCostsBox from "./Partials/QuoteTariffEstimatedCostsBox";
import QuoteTariffInfoBox from "./Partials/QuoteTariffInfoBox";
import SupplierLogo from "./Partials/SupplierLogo";
import SupplierTariffDetails from "./Partials/SupplierTariffDetails";

const elecData = [
  {
    quoteKey: "tilSupplierNameElec",
    label: "Supplier",
  },
  {
    quoteKey: "tilTariffNameElec",
    label: "Tariff",
  },
  {
    quoteKey: "tilTariffTypeElec",
    label: "Tariff type",
  },
  {
    quoteKey: "tilPayTypeElec",
    label: "Payment method",
  },
  {
    quoteKey: "tilUnitRateElec",
    label: "Unit rate",
    format: (value) => (value ? `${value.toFixed(2)}p / kWh` : "N/A"),
  },
  {
    quoteKey: "tilUnitRateE7Elec",
    label: "Night rate",
    format: (value) => (value ? `${value.toFixed(2)}p / kWh` : "N/A"),
  },
  {
    quoteKey: "tilStandingChargeElec",
    label: "Standing charges",
    format: (value) => (value ? `${value.toFixed(2)}p / day` : "N/A"),
  },
  {
    quoteKey: "tilExitFeeElec",
    label: "Exit fees",
    format: (value) => (value ? `£${value.toFixed(2)}` : "N/A"),
  },
  {
    quoteKey: "elecPriceGuaranteedUntil",
    label: "Price guaranteed until",
    format: (value) => (value ? value : "N/A"),
  },
  {
    quoteKey: "tilTariffEndDateElec",
    label: "Tariff ends on",
    format: (value) => {
      const date = new Date(value);
      return isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
    },
  },
];

const gasData = [
  {
    quoteKey: "tilSupplierNameGas",
    label: "Supplier",
  },
  {
    quoteKey: "tilTariffNameGas",
    label: "Tariff",
  },
  {
    quoteKey: "tilTariffTypeGas",
    label: "Tariff type",
  },
  {
    quoteKey: "tilPayTypeGas",
    label: "Payment method",
  },
  {
    quoteKey: "tilUnitRateGas",
    label: "Unit rate",
    format: (value) => (value ? `${value.toFixed(2)}p / kWh` : "N/A"),
  },
  {
    quoteKey: "tilStandingChargeGas",
    label: "Standing charges",
    format: (value) => (value ? `${value.toFixed(2)}p / day` : "N/A"),
  },
  {
    quoteKey: "tilExitFeeGas",
    label: "Exit fees",
    format: (value) => (value ? `£${value.toFixed(2)}` : "N/A"),
  },
  {
    quoteKey: "gasPriceGuaranteedUntil",
    label: "Price guaranteed until",
    format: (value) => (value ? value : "N/A"),
  },
  {
    quoteKey: "tilTariffEndDateGas",
    label: "Tariff ends on",
    format: (value) => {
      const date = new Date(value);
      return isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
    },
  },
];

const defaultFormat = (value) => value || "N/A";

const QuoteDetails = ({ quote, setDetailsModalOpen }) => {
  const [currentTab, setCurrentTab] = useState(
    normaliseFuelType(quote?.fuelType) === SUPPLIER_FUEL_TYPE.ELECGAS
      ? "electricity"
      : normaliseFuelType(quote?.fuelType)
  );

  const faqs = [
    {
      question: EN.quotes.how_was_calculated_text,
      answer: EN.quotes.how_was_calculated_answer,
    },
    {
      question: `About ${quote?.supplierName}`,
      answer: quote?.synopsisText,
    },
  ];

  const {
    existingTariffElec,
    existingTariffGas,
    estimatedMonthlyKwhsElec,
    estimatedMonthlyKwhsGas,
    estimatedMonthlySpendElec,
    estimatedMonthlySpendGas,
  } = useTariff();

  let tabs = [];
  switch (normaliseFuelType(quote?.fuelType)) {
    case SUPPLIER_FUEL_TYPE.ELEC:
      tabs = [{ id: "electricity", name: "Electricity", current: true }];
      break;
    case SUPPLIER_FUEL_TYPE.GAS:
      tabs = [{ id: "gas", name: "Gas", current: true }];
      break;
    default:
      tabs = [
        {
          id: "electricity",
          name: "Electricity",
          current: currentTab === "electricity",
        },
        { id: "gas", name: "Gas", current: currentTab === "gas" },
      ];
  }

  const currentData = currentTab === "electricity" ? elecData : gasData;
  const existingTariff =
    currentTab === "electricity" ? existingTariffElec : existingTariffGas;

  const elecSavings =
    parseFloat(estimatedMonthlySpendElec) -
    parseFloat(quote?.newSpendMonthlyDDElec);

  const gasSavings =
    parseFloat(estimatedMonthlySpendGas) -
    parseFloat(quote?.newSpendMonthlyDDGas);

  const potentialSavings =
    currentTab === "electricity" ? elecSavings : gasSavings;

  return (
    <div className="p-[30px] pt-[18px]">
      <div className="pb-3 border-b border-[#afb6b9] flex justify-between items-center">
        <h1 className="text-[30px] font-bold text-primary">
          {EN.quotes.tariff_details_text}
        </h1>
        <button
          className="w-[30px] h-[30px] flex items-center justify-center rounded-full bg-[#E7E9EA]"
          onClick={() => setDetailsModalOpen(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="size-6 text-black"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="mt-5">
        {quote?.supplierImageUrl && (
          <SupplierLogo
            src={quote?.supplierImageUrl}
            alt={quote?.supplierName}
          />
        )}
        <SupplierTariffDetails
          supplierName={quote?.supplierName}
          tariffName={quote?.tariffName}
        />
        <QuoteTariffEstimatedCostsBox quote={quote} />
        <QuoteTariffInfoBox quote={quote} />
        <div className="mt-6">
          <Tabs tabs={tabs} setCurrentTab={setCurrentTab} align="left" />
        </div>
        <table className="mt-6 w-full table-fixed border-b border-[#afb6b9]">
          <thead className="border-y border-[#afb6b9]">
            <tr className="align-top">
              <th className="py-5"></th>
              <th className="py-5 font-bold leading-[1.5]">
                {EN.quotes.new_tariff_text}
              </th>
              <th className="py-5 font-bold">
                {EN.quotes.current_tariff_text}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#afb6b9]">
            {Object.keys(currentData).map((key) => (
              <tr className="align-top" key={key}>
                <td className="py-5 font-bold">{currentData[key].label}</td>
                <td className="py-5">
                  {currentData[key].format && quote[currentData[key].quoteKey]
                    ? currentData[key].format(quote[currentData[key].quoteKey])
                    : defaultFormat(quote[currentData[key].quoteKey])}
                </td>
                <td className="py-5">
                  {" "}
                  {existingTariff[currentData[key].quoteKey] &&
                  currentData[key].quoteKey &&
                  currentData[key].format
                    ? currentData[key].format(
                        existingTariff[currentData[key].quoteKey]
                      )
                    : defaultFormat(existingTariff[currentData[key].quoteKey])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-8">
          <h2 className="text-[22px] font-bold mb-4">
            {EN.quotes.your_potential_savings_text}
          </h2>

          <div className="space-y-3">
            <div className="flex justify-between items-center text-md font-bold">
              <span>{EN.quotes.your_current_monthly_usage_text}</span>
              <span>
                {currentTab === "electricity"
                  ? estimatedMonthlyKwhsElec
                  : estimatedMonthlyKwhsGas}{" "}
                kWh
              </span>
            </div>

            <div className="flex justify-between items-center text-md font-bold">
              <span>{EN.quotes.your_current_monthly_payment_text}</span>
              <span>
                £
                {currentTab === "electricity"
                  ? parseFloat(estimatedMonthlySpendElec).toFixed(2)
                  : parseFloat(estimatedMonthlySpendGas).toFixed(2)}
              </span>
            </div>

            <div className="border-t border-[#afb6b9] my-4"></div>

            <div className="flex justify-between items-center text-md font-bold">
              <span>{EN.quotes.with_this_tariff_text}</span>
              <span>
                £
                {(currentTab === "electricity"
                  ? quote?.newSpendMonthlyDDElec
                  : quote?.newSpendMonthlyDDGas
                ).toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between items-center text-md font-bold">
              <span>{EN.quotes.estimated_savings_text}</span>
              <span
                className={`text-md font-bold ${
                  potentialSavings < 0 ? "text-red-600" : "text-green-600"
                }`}
              >
                £{potentialSavings.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        <dl className="mt-8 space-y-6">
          {faqs.map((faq) => (
            <Disclosure key={faq.question} as="div" className="">
              <dt>
                <DisclosureButton className="group flex w-full items-start justify-start text-left">
                  <span className="mr-2 flex h-7 items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                      stroke="currentColor"
                      className="size-5 group-data-[open]:rotate-180 text-primary"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m19.5 8.25-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </span>
                  <span className="text-primary underline">{faq.question}</span>
                </DisclosureButton>
              </dt>
              <DisclosurePanel as="dd" className="mt-2 px-[28px]">
                <p>{faq.answer}</p>
              </DisclosurePanel>
            </Disclosure>
          ))}
        </dl>
        <div className="mt-6 border-t border-[#afb6b9] pt-6 flex justify-end">
          {quote?.isSwitchable ? (
            <SwitchNowButton quote={quote} />
          ) : (
            <p className="text-center py-1">{EN.quotes.contact_supplier}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteDetails;
